import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';

interface PlannerSlider {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  valueSuffix?: string;
  secondaryValue?: number;
  secondaryPrefix?: string;
  secondarySuffix?: string;
  secondaryDecimals?: number;
}

interface PlannerField {
  key: string;
  label: string;
  type?: string;
  value: string | number;
  editable?: boolean;
  helperText?: string;
}

interface PlannerResultRow {
  label: string;
  value: number | string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  tone?: 'positive' | 'negative' | 'neutral' | string;
}

@Component({
  selector: 'app-price-planner',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSliderModule, MatButtonModule],
  templateUrl: './price-planner.component.html',
  styleUrl: './price-planner.component.scss',
})
export class PricePlannerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;

  title = 'Price Planner';
  subtitle = '';
  sliders: PlannerSlider[] = [];
  fields: PlannerField[] = [];
  resultRows: PlannerResultRow[] = [];
  actionButton: any = null;
  loading = false;
  submitting = false;
  errorMessage = '';
  actionMessage = '';

  private readonly destroy$ = new Subject<void>();
  private readonly configService = inject(ConfigService);

  ngOnInit(): void {
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.loadConfig();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByKey(_index: number, item: { key?: string; label?: string }): string {
    return String(item.key || item.label || _index);
  }

  onSliderChange(slider: PlannerSlider, value: number): void {
    slider.value = Number(value);
  }

  onAction(): void {
    if (!this.actionButton?.apiUrl || this.submitting) {
      return;
    }

    this.submitting = true;
    this.actionMessage = '';
    this.errorMessage = '';

    this.configService
      .getConfig(this.actionButton.apiUrl, { defaultRequestData: this.buildSubmitPayload() }, {}, this.actionButton.method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.submitting = false;
          this.actionMessage = this.actionButton?.successMessage || 'Changes applied successfully.';
        },
        error: () => {
          this.submitting = false;
          this.errorMessage = this.actionButton?.errorMessage || 'Unable to apply changes.';
        },
      });
  }

  resultToneClass(tone?: string): string {
    switch ((tone || '').toLowerCase()) {
      case 'positive':
        return 'is-positive';
      case 'negative':
        return 'is-negative';
      default:
        return 'is-neutral';
    }
  }

  formatDisplayValue(value: string | number, prefix = '', suffix = '', decimals = 0): string {
    if (typeof value === 'number') {
      return `${prefix}${new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value)}${suffix}`;
    }
    return `${prefix}${value}${suffix}`;
  }

  private loadConfig(): void {
    this.errorMessage = '';
    this.actionMessage = '';

    if (!this.data?.apiUrl) {
      this.applyConfig(this.data || {});
      return;
    }

    this.loading = true;
    this.configService
      .getConfig(this.data.apiUrl, this.data, {}, this.data.method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.applyConfig(response || this.data);
          this.loading = false;
        },
        error: () => {
          this.applyConfig(this.data || {});
          this.loading = false;
          this.errorMessage = 'Unable to load price planner data.';
        },
      });
  }

  private applyConfig(source: any): void {
    this.title = source?.title || this.data?.title || 'Price Planner';
    this.subtitle = source?.subtitle || this.data?.subtitle || '';
    this.sliders = Array.isArray(source?.sliders) ? source.sliders.map((slider: any) => ({
      key: String(slider?.key || slider?.label || 'slider'),
      label: String(slider?.label || 'Slider'),
      min: Number(slider?.min ?? 0),
      max: Number(slider?.max ?? 100),
      step: Number(slider?.step ?? 1),
      value: Number(slider?.value ?? 0),
      valueSuffix: slider?.valueSuffix,
      secondaryValue: slider?.secondaryValue,
      secondaryPrefix: slider?.secondaryPrefix,
      secondarySuffix: slider?.secondarySuffix,
      secondaryDecimals: slider?.secondaryDecimals,
    })) : [];
    this.fields = Array.isArray(source?.fields) ? source.fields.map((field: any) => ({
      key: String(field?.key || field?.label || 'field'),
      label: String(field?.label || 'Field'),
      type: field?.type || 'text',
      value: field?.value ?? '',
      editable: field?.editable !== false,
      helperText: field?.helperText,
    })) : [];
    this.resultRows = Array.isArray(source?.resultRows) ? source.resultRows.map((row: any) => ({
      label: String(row?.label || 'Result'),
      value: typeof row?.value === 'number' ? row.value : Number.isFinite(Number(row?.value)) ? Number(row.value) : String(row?.value ?? ''),
      prefix: row?.prefix,
      suffix: row?.suffix,
      decimals: row?.decimals,
      tone: row?.tone,
    })) : [];
    this.actionButton = source?.actionButton || this.data?.actionButton || null;
  }

  private buildSubmitPayload(): Record<string, unknown> {
    return {
      sliders: this.sliders.reduce<Record<string, number>>((acc, slider) => {
        acc[slider.key] = slider.value;
        return acc;
      }, {}),
      fields: this.fields.reduce<Record<string, string | number>>((acc, field) => {
        acc[field.key] = field.value;
        return acc;
      }, {}),
    };
  }
}
